import { getLogger } from '@packages/core/logging';
import { type AccountEntity, SearchAccounts } from '@packages/domain/accounts';
import { container } from '@web/services/container';
import { useCallback, useState } from 'react';

const logger = getLogger('techline');

/**
 * A selectable search option representing a retailer account. This is a presentation-layer type
 * shaped for react-select's `AsyncSelect` component (`{ label, value }`). It does not belong in
 * the domain/use-case layer because the `label`/`value` structure is a UI concern.
 */
export type AccountSearchOption = {
    /** Display label combining the user's name, username, and job title. */
    label: string;

    /** The full account entity for the matched user. */
    value: AccountEntity;
};

// Operations are prefixed with $ to make them visually distinct in this file.
const [$searchTechlineAccounts] = container.resolveOperations(SearchAccounts);

/**
 * Provides async account search for react-select's `AsyncSelect` component, with loading state.
 * Delegates the API search to the `SearchTechlineAccounts` use case, then maps the raw account
 * records into `{ label, value }` options for the select dropdown. The mapping is intentionally
 * here (not in the use case) because the `label`/`value` shape is a presentation concern.
 *
 * @param retailerNumber - the retailer code to scope account searches to
 * @returns hook state and handlers for async account search
 */
export function useAccountSearch(retailerNumber: string) {
    // State
    const [isSearching, setIsSearching] = useState(false);

    // Handlers
    /**
     * Callback for `AsyncSelect`'s `loadOptions` prop. Searches for accounts matching the user's
     * input text, maps results to select options, and manages loading state. Calls both the
     * callback (for react-select's legacy API) and returns the results (for the promise API).
     *
     * @param inputValue - the text the user typed into the search field
     * @param callback - react-select's callback to invoke with the resulting options
     * @returns the matching account options
     */
    const fetchAccountsAsSelectOptions = useCallback(
        async (
            inputValue: string,
            callback: (options: AccountSearchOption[]) => void
        ): Promise<AccountSearchOption[]> => {
            setIsSearching(true);
            try {
                // Search for accounts and map to select options
                const accounts = await $searchTechlineAccounts({ inputValue, retailerNumber });
                const options = accounts.map(mapAccountToSearchOption);
                callback(options);
                return options;
            } catch (error) {
                logger.error('Failed to fetch account search results', { error });
                callback([]);
                return [];
            } finally {
                setIsSearching(false);
            }
        },
        [retailerNumber]
    );

    return {
        /**
         * Callback for `AsyncSelect`'s `loadOptions` prop. Searches for accounts at the given
         * retailer and returns them as select options.
         */
        fetchAccountsAsSelectOptions,

        /** Whether an account search request is currently in flight. */
        isSearching,
    };
}

/**
 * Map an account record from the API into a react-select option. Builds a human-readable label
 * from the account's name, SOA username, and job title so dropdown results are easily scannable.
 *
 * @param account - the account record from the retailer search API
 * @returns a select option with a formatted label and the original account as the value
 * @example
 * ```ts
 * const option = mapAccountToSearchOption(account);
 * // { label: 'Luke Chavers (lchavers) - Engineer', value: account }
 * ```
 */
function mapAccountToSearchOption(account: AccountEntity): AccountSearchOption {
    const label = `${account.firstName} ${account.lastName} (${account.soaUsername}) - ${account.jobTitle}`;
    return { label, value: account };
}
